import { normalizeOpeningPeriods } from '../../utils/openingHours';

export const LOAD_COLORS = {
  green: '#5ac83b', orange: '#ff8a00', red: '#ff4b35',
  claret: '#b62815', blue: '#1677ff', gray: '#7d8794',
};

export const loadPresentation = (place, t) => {
  const hasOpeningHours = normalizeOpeningPeriods(place?.openingHours).length > 0;
  const rawScore = place?.load?.loadScore;
  const score = rawScore == null || rawScore === '' ? Number.NaN : Number(rawScore);

  if (hasOpeningHours && place?.openingHours?.openNow === false) {
    return { color: LOAD_COLORS.gray, label: t('load.closed'), score: null };
  }
  if (
    place?.loadTrackingEnabled === false ||
    !Number.isFinite(score)
  ) {
    return { color: LOAD_COLORS.blue, label: t('load.unknown'), score: null };
  }
  if (score <= 4) return { color: LOAD_COLORS.green, label: t('load.calm'), score };
  if (score <= 6) return { color: LOAD_COLORS.orange, label: t('load.medium'), score };
  if (score <= 8) return { color: LOAD_COLORS.red, label: t('load.busy'), score };
  return { color: LOAD_COLORS.claret, label: t('load.veryBusy'), score };
};

export const lighten = (hex, amount = 0.65) => {
  const value = hex.replace('#', '');
  if (!/^[0-9a-f]{6}$/i.test(value)) return hex;
  return `#${[0, 2, 4].map((offset) => {
    const channel = parseInt(value.slice(offset, offset + 2), 16);
    return Math.round(channel + (255 - channel) * amount)
      .toString(16)
      .padStart(2, '0');
  }).join('')}`;
};

export const hasPolygon = (place) =>
  (
    (place?.footprint?.type === 'Polygon' &&
      Array.isArray(place?.footprint?.coordinates?.[0]) &&
      place.footprint.coordinates[0].length >= 4) ||
    (place?.footprint?.type === 'MultiPolygon' &&
      Array.isArray(place?.footprint?.coordinates?.[0]?.[0]) &&
      place.footprint.coordinates[0][0].length >= 4)
  );

export const footprintCoordinates = (footprint) => {
  const polygons = footprint?.type === 'MultiPolygon'
    ? footprint.coordinates
    : footprint?.type === 'Polygon'
      ? [footprint.coordinates]
      : [];
  return (polygons || []).flatMap((polygon) => (polygon || []).flat());
};

const normalizePolygonCoordinates = (polygon) => {
  if (!Array.isArray(polygon) || !polygon.length) return null;
  const rings = polygon.map((ring) => {
    if (!Array.isArray(ring) || ring.length < 4) return null;
    const points = ring.map((point) => {
      const longitude = Number(point?.[0]);
      const latitude = Number(point?.[1]);
      return Number.isFinite(longitude) && Number.isFinite(latitude) &&
        longitude >= -180 && longitude <= 180 && latitude >= -90 && latitude <= 90
        ? [longitude, latitude]
        : null;
    });
    if (points.some((point) => point === null)) return null;
    const first = points[0];
    const last = points[points.length - 1];
    if (first[0] !== last[0] || first[1] !== last[1]) points.push([...first]);
    return points;
  });
  return rings.some((ring) => ring === null) ? null : rings;
};

export const placeLabel = (place) =>
  `${place?.name || place?.displayName || ''}${place?.branchName ? ` - ${place.branchName}` : ''}`;
export const placesToGeoJson = (places, t, revealProgress = 1) => ({
  type: 'FeatureCollection',
  features: places.filter(hasPolygon).flatMap((place) => {
    const load = loadPresentation(place, t);
    const heightLevel = Number.isInteger(place.footprintHeightLevel)
      ? Math.min(3, Math.max(0, place.footprintHeightLevel))
      : 1;
    const polygons = place.footprint.type === 'MultiPolygon'
      ? place.footprint.coordinates
      : [place.footprint.coordinates];

    return polygons.flatMap((polygon, polygonIndex) => {
      const coordinates = normalizePolygonCoordinates(polygon);
      if (!coordinates) {
        console.warn('Invalid place footprint skipped', {
          placeId: place.id,
          polygonIndex,
        });
        return [];
      }
      return [{
        type: 'Feature',
        id: `${place.id}-${polygonIndex}`,
        properties: {
          placeId: place.id,
          name: placeLabel(place),
          color: load.color,
          fillColor: lighten(load.color),
          heightMultiplier: [0, 0.5, 0.875, 1.25][heightLevel],
          revealProgress,
        },
        geometry: {
          type: 'Polygon',
          coordinates,
        },
      }];
    });
  }),
});
